import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { ColorValues, Flex, Typography } from '@banhala/ui-kit';
import { useCancelFeedback } from 'shared/src/hooks/useFeedback';
import type { FeedbackCacheItem } from 'shared/src/api/feedbackService/types';
import type { IScreensLogging } from 'shared/src/api/types';
import { useLogClickUndoFeedback } from './hooks';

type GoodsCardFeedbackOverlayProps = {
  feedbackCache: FeedbackCacheItem;
  logging?: IScreensLogging;
  onCancelComplete: (goodsSno: number) => void;
};

export const GoodsCardFeedbackOverlay = ({
  feedbackCache,
  logging,
  onCancelComplete,
}: GoodsCardFeedbackOverlayProps) => {
  const { mutateAsync: cancelFeedback } = useCancelFeedback();
  const logClickUndoFeedback = useLogClickUndoFeedback();

  const handleCancel = useCallback(async () => {
    logClickUndoFeedback({
      logging,
      feedbackEventKey: feedbackCache.feedbackItem?.event_key ?? '',
    });

    try {
      await cancelFeedback({
        feedbackResultSno: feedbackCache.feedbackResultSno,
      });
    } finally {
      onCancelComplete(feedbackCache.goodsSno);
    }
  }, [feedbackCache, logging, logClickUndoFeedback, cancelFeedback, onCancelComplete]);

  return (
    <View style={[StyleSheet.absoluteFill, styles.overlay]}>
      <Flex flex={1} justifyContent="space-between" alignItems="center">
        {/* 감사 메시지 */}
        <Flex
          padding={{ horizontal: 12, vertical: 12 }}
          alignSelf="stretch"
        >
          <Typography type="body2-relaxed" color="content_always_white">
            {'감사합니다.\n의견을 반영하여 상품을 추천해 드릴게요.'}
          </Typography>
        </Flex>

        {/* 실행 취소 버튼 */}
        <Flex margin={{ bottom: 16 }}>
          <Flex
            width={76}
            height={32}
            alignItems="center"
            justifyContent="center"
            backgroundColorValue={ColorValues.gray60}
            borderRadius="xlarge"
            overflow="hidden"
            onPress={handleCancel}
          >
            <Typography type="subtitle3" color="content_always_white">
              실행 취소
            </Typography>
          </Flex>
        </Flex>
      </Flex>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 4,
    overflow: 'hidden',
  },
});
